"use client"

import React, { Component, ErrorInfo, ReactNode } from "react"
import { trackError } from "../lib/telemetry"

interface Props {
  children: ReactNode
  fallback?: ReactNode
  userId?: string
  sessionId?: string
}

interface State {
  hasError: boolean
  error?: Error
}

/**
 * Error boundary component with Azure Application Insights telemetry
 */
export class TelemetryErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Track the error with detailed context
    trackError(error, "React Error Boundary", {
      userId: this.props.userId,
      sessionId: this.props.sessionId,
      customProperties: {
        "error.componentStack": errorInfo.componentStack,
        "error.errorBoundary": true,
        "error.digest": (errorInfo as any).digest,
      },
    })

    // Log to console for development
    if (process.env.NODE_ENV === "development") {
      console.error("Error caught by boundary:", error)
      console.error("Error info:", errorInfo)
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        this.props.fallback || (
          <div style={{ padding: "20px", textAlign: "center" }}>
            <h2>Something went wrong.</h2>
            <p>
              We&apos;ve been notified of this error and are working to fix it.
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: undefined })
                window.location.reload()
              }}
            >
              Reload Page
            </button>
          </div>
        )
      )
    }

    return this.props.children
  }
}

/**
 * HOC for wrapping components with error boundary and telemetry
 */
export function withTelemetryErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallback?: ReactNode,
) {
  return function TelemetryWrappedComponent(
    props: P & { userId?: string; sessionId?: string },
  ) {
    const { userId, sessionId, ...componentProps } = props
    return (
      <TelemetryErrorBoundary
        fallback={fallback}
        userId={userId}
        sessionId={sessionId}
      >
        <WrappedComponent {...(componentProps as P)} />
      </TelemetryErrorBoundary>
    )
  }
}
